import React, { Component } from 'react';
import classNames from 'classnames';
import { withContext } from '../utils/StateProvider';

// Class for a square, matching the minimap's colours. Shared with the map
// editor, so a painted map looks the same in both.
export function tileClass(tile) {
  if (tile === 1 || tile === 2) return 'wall';
  if (tile === 3) return 'door-horizontal-closed';
  if (tile === 4) return 'door-horizontal-open';
  if (tile === 5) return 'door-vertical-closed';
  if (tile === 6) return 'door-vertical-open';
  if (tile === 7) return 'stairs-tile';
  if (tile === 9) return 'trainer-tile';
  if (tile === 12) return 'magic-tile';
  if (tile === 13) return 'market-tile';
  if (tile === 14) return 'temple-tile';
  if (tile === 15) return 'tavern-tile';
  if (tile === 10) return 'stairs-down-tile';
  if (tile === 11) return 'door-broken';
  return 'grass';
}

// The grid is laid out to whatever level is open: a dungeon floor is 40
// squares across, the town a third of that, and a small map is drawn with
// bigger squares rather than left rattling around in the corner of the panel.
const MAP_WIDTH = 600;
const MAX_CELL = 32;

function cellSize(across) {
  return Math.max(6, Math.min(MAX_CELL, Math.floor(MAP_WIDTH / across)));
}

// The whole level as far as it has been explored, opened with the MAP button
class MapScreen extends Component {

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeydown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown = (e) => {
    if (e.key === 'Escape' && this.props.context.mapOpen) this.props.context.closeMap();
  }

  // The town has a name; a dungeon floor has a number
  title() {
    const { run, town } = this.props.context;
    if (town) return run && run.town ? run.town : 'The Town';
    return run ? `Level ${run.level} of ${run.levels}` : 'The Dungeon';
  }

  render() {
    const { mapOpen, player_map, player_coords, player_heading, explored, run, closeMap } = this.props.context;
    if (!mapOpen) return null;

    // The arrow image is drawn for a screen where east and west are swapped
    const arrowHeading = player_heading === 90 ? 270 : player_heading === 270 ? 90 : player_heading;
    const seen = explored.flat().filter(Boolean).length;
    const walkable = player_map.flat().filter((tile) => tile !== 1 && tile !== 2).length;
    const across = player_map[0].length;
    const cell = cellSize(across);
    const layout = {
      gridTemplateColumns: `repeat(${across}, ${cell}px)`,
      gridAutoRows: `${cell}px`,
      fontSize: `${Math.round(cell * 0.7)}px`,
      lineHeight: `${cell}px`,
    };

    return (
      <div id="mapScreen">
        <div className="map-panel">
          <div className="sheet-header">
            <div>
              <h1>{this.title()}</h1>
              <div className="sheet-subtitle">
                {Math.round((seen / walkable) * 100)}% explored{run ? ` · seed ${run.seed}` : ''}
              </div>
            </div>
            <div className="sheet-close" onClick={closeMap}>CLOSE (ESC)</div>
          </div>

          <div className="map-grid" style={layout}>
            {player_map.map((row, y) => row.map((tile, x) => {
              const here = player_coords[0] === x && player_coords[1] === y;
              if (!here && !explored[y][x]) {
                return <div key={`${x}:${y}`} className="map-cell unexplored" />;
              }
              // Everything explored is drawn the same: the map is what the
              // party remembers, not what it can see from here
              const classes = classNames('map-cell', tileClass(tile));

              if (here) {
                return (
                  <div key={`${x}:${y}`} className={classNames(classes, 'player')}>
                    <img style={{ rotate: arrowHeading + 'deg' }} src="./assets/minimap_arrow.png" alt="party" />
                  </div>
                );
              }
              if (tile === 7) return <div key={`${x}:${y}`} className={classes}>▲</div>;
              if (tile === 10) return <div key={`${x}:${y}`} className={classes}>▼</div>;
              if (tile === 8) return <div key={`${x}:${y}`} className={classNames(classes, 'chest-tile')} />;
              return <div key={`${x}:${y}`} className={classes} />;
            }))}
          </div>

          <div className="map-legend">
            <span><i className="swatch grass" /> floor</span>
            <span><i className="swatch wall" /> wall</span>
            <span><i className="swatch door-horizontal-closed" /> door</span>
            <span><i className="swatch door-broken" /> broken</span>
            <span><i className="swatch chest-tile" /> chest</span>
            <span><i className="swatch stairs-tile" /> up</span>
            <span><i className="swatch stairs-down-tile" /> down</span>
            <span><i className="swatch trainer-tile" /> trainer</span>
            <span><i className="swatch temple-tile" /> temple</span>
            <span><i className="swatch magic-tile" /> magic</span>
            <span><i className="swatch market-tile" /> market</span>
            <span><i className="swatch tavern-tile" /> tavern</span>
          </div>
        </div>
      </div>
    );
  }
}

export default withContext(MapScreen);
